
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FeedbackMessage, FeedbackCategoryTag } from "@/types/feedback";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const useUpdateMessage = (messages: FeedbackMessage[] = []) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const updateMessage = useMutation({
    mutationFn: async ({ id, message, category_tag }: { id: string, message: string, category_tag?: FeedbackCategoryTag }) => {
      if (!user) {
        console.error("No authenticated user found when trying to update message");
        throw new Error("Je moet ingelogd zijn om een bericht te bewerken");
      }

      console.log("Attempting to update message:", id);
      console.log("Current user:", user);
      
      // Get the message to be updated
      const existingMessage = messages.find(m => m.id === id);
      if (!existingMessage) {
        console.error("Message not found in local data:", id);
        throw new Error("Bericht niet gevonden");
      }
      
      console.log("Message to update:", existingMessage);
      console.log("Message user_id:", existingMessage.user_id);
      console.log("Current user id:", user.id);
      
      // The RLS policy will enforce ownership, but we'll check client-side as well
      if (existingMessage.user_id !== user.id) {
        console.error("User trying to update message they don't own");
        throw new Error("Je kunt alleen je eigen berichten bewerken");
      }

      // Always use an explicit filter to ensure we're only operating on the user's own messages
      const { data, error } = await supabase
        .from('feedback_messages')
        .update({
          message,
          category_tag: category_tag || null,
          edited_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('user_id', user.id) // Add this explicit filter for extra security
        .select('*');
      
      if (error) {
        console.error("Supabase update error:", error);
        throw new Error(`Fout bij het bijwerken van bericht: ${error.message}`);
      }
      
      if (!data || data.length === 0) {
        console.error("No data returned from update operation");
        throw new Error("Kon het bericht niet bijwerken. Mogelijk heb je geen rechten of bestaat het bericht niet meer.");
      }
      
      console.log("Update successful:", data);
      
      return data[0] as FeedbackMessage;
    },
    onSuccess: (data) => {
      console.log("Message updated successfully:", data);
      queryClient.invalidateQueries({ queryKey: ['feedback-messages'] });
      toast.success('Bericht succesvol bijgewerkt');
    },
    onError: (error: any) => {
      console.error('Error updating feedback message:', error);
      toast.error(`Fout bij het bijwerken van bericht: ${error.message || "Onbekende fout"}`);
    }
  });

  return { updateMessage, user };
};
